import { useState, useEffect, useMemo } from 'react';
import {
  collection, query, where, onSnapshot, doc, writeBatch, serverTimestamp,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui';
import BankMoveModal from '../components/BankMoveModal';
import { fmtMoney } from '../utils/currency';
import type { BankAccount, BankTransaction, BankStatement } from '../types';

function todayISO() { return new Date().toISOString().slice(0, 10); }
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const fmtDate = (s?: string) =>
  s ? new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const signed = (t: BankTransaction) => (t.direction === 'in' ? (t.amount ?? 0) : -(t.amount ?? 0));

export default function BankReconcilePage() {
  const { user, business } = useAuth();
  const money = (v: number, cur: string) => fmtMoney(v ?? 0, cur);

  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [txns, setTxns]         = useState<BankTransaction[]>([]);
  const [statements, setStatements] = useState<BankStatement[]>([]);
  const [accountId, setAccountId] = useState('');

  const [stmtDate, setStmtDate] = useState(todayISO());
  const [openingOverride, setOpeningOverride] = useState<string | null>(null);
  const [closingBalance, setClosingBalance] = useState('');
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [charging, setCharging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    const u = onSnapshot(collection(db, 'bankAccounts'),
      snap => setAccounts(snap.docs.map(d => ({ id: d.id, ...d.data() } as BankAccount)).filter(a => a.isActive !== false)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))), () => {});
    return u;
  }, [business]);

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business || !accountId) return;
    const u1 = onSnapshot(
      query(collection(db, 'bankTransactions'), where('accountId', '==', accountId)),
      snap => setTxns(snap.docs.map(d => ({ id: d.id, ...d.data() } as BankTransaction))), () => {});
    const u2 = onSnapshot(
      query(collection(db, 'bankStatements'), where('bankAccountId', '==', accountId)),
      snap => setStatements(snap.docs.map(d => ({ id: d.id, ...d.data() } as BankStatement))
        .sort((a, b) => b.statementDate.localeCompare(a.statementDate))), () => {});
    return () => { u1(); u2(); };
  }, [business, accountId]);

  const account = accounts.find(a => a.id === accountId) ?? null;
  const currency = account?.currency ?? 'USD';

  // Opening balance defaults to the last reconciled statement's closing (or the
  // account's own opening), and the user can override it — no effect needed.
  const defaultOpening = round2(statements[0]?.closingBalance ?? account?.openingBalance ?? 0);
  const openingBalance = openingOverride ?? String(defaultOpening);

  function pickAccount(id: string) {
    setAccountId(id);
    setTxns([]); setStatements([]);
    setOpeningOverride(null);
    setTicked(new Set());
    setClosingBalance('');
    setMsg(''); setErr('');
  }

  const unreconciled = useMemo(
    () => txns.filter(t => (t.clearedStatus ?? 'uncleared') !== 'reconciled' && t.date <= stmtDate)
      .sort((a, b) => a.date.localeCompare(b.date)),
    [txns, stmtDate],
  );

  const opening = round2(parseFloat(openingBalance) || 0);
  const closing = round2(parseFloat(closingBalance) || 0);
  const clearedSum = useMemo(
    () => round2(unreconciled.filter(t => ticked.has(t.id)).reduce((s, t) => s + signed(t), 0)),
    [unreconciled, ticked],
  );
  const difference = round2(opening + clearedSum - closing);
  const canReconcile = !!closingBalance && Math.abs(difference) < 0.005;

  function toggle(id: string) {
    setTicked(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }

  async function reconcile() {
    if (!canReconcile) return;
    setSaving(true); setErr(''); setMsg('');
    const db = getClientDb();
    if (!db || !business || !account) { setErr('Not connected.'); setSaving(false); return; }
    try {
      const stmtRef = doc(collection(db, 'bankStatements'));
      const batch = writeBatch(db);
      const tickedTx = unreconciled.filter(t => ticked.has(t.id));
      batch.set(stmtRef, {
        businessId: business.id, bankAccountId: account.id, bankAccountName: account.name,
        currency: account.currency, statementDate: stmtDate,
        openingBalance: opening, closingBalance: closing,
        clearedTotal: clearedSum, txnCount: tickedTx.length,
        status: 'reconciled',
        createdAt: serverTimestamp(), createdBy: user!.id,
      });
      tickedTx.forEach(t => batch.update(doc(db, 'bankTransactions', t.id), {
        clearedStatus: 'reconciled', clearedDate: stmtDate, statementId: stmtRef.id,
      }));
      await batch.commit();
      setMsg(`Reconciled ${tickedTx.length} transaction${tickedTx.length === 1 ? '' : 's'} as of ${fmtDate(stmtDate)}.`);
      setTicked(new Set()); setClosingBalance('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not reconcile.');
    }
    setSaving(false);
  }

  return (
    <AppLayout
      title="Bank Reconciliation"
      subtitle="Tick off transactions against a bank statement until it balances."
      toolbar={<>
        {/* The statement you're matching: account, closing date, opening
            and closing balance, each a labelled box in the header. */}
        <select className="tb-select" value={accountId} onChange={e => pickAccount(e.target.value)} aria-label="Account" style={{ minWidth: 200 }}>
          <option value="">Choose an account…</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
        </select>
        {account && (
          <>
            <label className="tb-field">
              <span className="tb-field-label">Statement date</span>
              <input type="date" value={stmtDate} max={todayISO()} onChange={e => setStmtDate(e.target.value)} />
            </label>
            <label className="tb-field" title={statements[0] ? `Last reconciled ${fmtDate(statements[0].statementDate)}` : undefined}>
              <span className="tb-field-label">Opening {currency}</span>
              <input type="number" step="0.01" className="no-spinner" value={openingBalance} onChange={e => setOpeningOverride(e.target.value)} style={{ width: 110 }} />
            </label>
            <label className="tb-field">
              <span className="tb-field-label">Closing {currency}</span>
              <input type="number" step="0.01" className="no-spinner" value={closingBalance} onChange={e => setClosingBalance(e.target.value)} placeholder="from statement" style={{ width: 120 }} />
            </label>
          </>
        )}
      </>}
      headerActions={account ? (<>
        <Button variant="secondary" size="sm" onClick={() => setCharging(true)} style={{ height: 34, boxSizing: 'border-box' }}>Add bank charge</Button>
        <Button variant="primary" size="sm" loading={saving} disabled={!canReconcile} onClick={reconcile} style={{ height: 34, boxSizing: 'border-box' }}>Reconcile</Button>
      </>) : undefined}
    >
      {(msg || err) && <div className={err ? 'error-msg' : 'info-msg'}>{err || msg}</div>}

      {!account ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px', fontSize: 13, color: 'var(--text-3)' }}>
          Pick an account to start reconciling.
        </div>
      ) : (
        <>
          <div className="card" style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
            <div><div style={{ fontSize: 11, color: 'var(--text-3)' }}>Opening</div><div style={{ fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(opening, currency)}</div></div>
            <div style={{ color: 'var(--text-3)' }}>+</div>
            <div><div style={{ fontSize: 11, color: 'var(--text-3)' }}>Ticked ({ticked.size})</div><div style={{ fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(clearedSum, currency)}</div></div>
            <div style={{ color: 'var(--text-3)' }}>−</div>
            <div><div style={{ fontSize: 11, color: 'var(--text-3)' }}>Statement closing</div><div style={{ fontFamily: 'var(--font-num)', fontWeight: 700 }}>{closingBalance ? money(closing, currency) : '—'}</div></div>
            <div style={{ color: 'var(--text-3)' }}>=</div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>Difference</div>
              <div style={{ fontFamily: 'var(--font-num)', fontWeight: 800, color: canReconcile ? 'var(--success)' : 'var(--danger)' }}>{money(difference, currency)}</div>
            </div>
          </div>

          <div className="card" style={{ padding: 0 }}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}></th>
                    <th style={{ width: 110 }}>Date</th>
                    <th>Description</th>
                    <th style={{ width: 130 }}>Category</th>
                    <th style={{ textAlign: 'right', width: 130 }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {unreconciled.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: '24px 0', fontSize: 12, color: 'var(--text-3)' }}>
                      Nothing left to reconcile up to this date.
                    </td></tr>
                  ) : unreconciled.map(t => (
                    <tr key={t.id} onClick={() => toggle(t.id)} style={{ cursor: 'pointer', background: ticked.has(t.id) ? 'var(--primary-light)' : undefined }}>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={ticked.has(t.id)} onChange={() => toggle(t.id)} onClick={e => e.stopPropagation()} />
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{fmtDate(t.date)}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{t.description}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-3)' }}>{t.category || '—'}</td>
                      <td style={{ textAlign: 'right', fontSize: 12, fontFamily: 'var(--font-num)', fontWeight: 600, color: t.direction === 'in' ? 'var(--success)' : 'var(--danger)' }}>
                        {t.direction === 'in' ? '+' : '−'}{money(t.amount, t.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {statements.length > 0 && (
            <div className="card" style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Past reconciliations
              </div>
              {statements.map(s => (
                <div key={s.id} style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--text-2)', padding: '4px 0' }}>
                  <span style={{ fontFamily: 'var(--font-num)' }}>{fmtDate(s.statementDate)}</span>
                  <span>closing {money(s.closingBalance, s.currency)}</span>
                  <span style={{ color: 'var(--text-3)' }}>{s.txnCount} txns</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {charging && account && (
        <BankMoveModal kind="charge" accounts={accounts} defaultAccountId={account.id} onClose={() => setCharging(false)} />
      )}
    </AppLayout>
  );
}
